import { State, StateGroup } from '../types/state.js';
import { StateTransition } from '../types/stateTransition.js';
import { StateHistoryEntry, StateHistoryTriggerSource } from '../types/stateHistory.js';

/**
 * Utility for mapping between Supabase Database rows and Frontend State objects.
 * Centralizing this ensures that schema changes only need to be updated in one place.
 */

// Explicit column allowlists. Keep these in sync with the corresponding `dbRowTo*`
// mapper below — every column the mapper reads must appear in the matching list,
// and no others. Using the allowlist with `.select(...)` instead of `.select('*')`
// means future internal/sensitive columns added to these tables (audit fields,
// billing markers, owner-only flags) will not auto-flow to authenticated clients.
export const STATE_COLUMNS = [
  'id',
  'team_id',
  'tag_id',
  'state_group_id',
  'name',
  'emoji',
  'color',
  'is_optimal',
  'sort_order',
  'created_at',
].join(', ');

export const STATE_TRANSITION_COLUMNS = [
  'id',
  'team_id',
  'tag_id',
  'from_state_id',
  'to_state_id',
  'from_handle',
  'to_handle',
  'task_text',
  'task_type',
  'created_at',
].join(', ');

export const STATE_GROUP_COLUMNS = [
  'id',
  'team_id',
  'name',
  'created_at',
].join(', ');

export const STATE_HISTORY_COLUMNS = [
  'id',
  'team_id',
  'tag_id',
  'from_state_id',
  'to_state_id',
  'changed_by_member_id',
  'changed_by_device_id',
  'trigger_source',
  'trigger_reference',
  'created_at',
].join(', ');

export const dbRowToState = (row: any): State => ({
  id: row.id,
  teamId: row.team_id,
  tagId: row.tag_id || null,
  stateGroupId: row.state_group_id || null,
  name: row.name,
  emoji: row.emoji || null,
  color: row.color || null,
  isOptimal: row.is_optimal ?? false,
  sortOrder: row.sort_order ?? null,
  createdAt: new Date(row.created_at),
});

export const mapStateToDb = (updates: Partial<State>): any => {
  const dbUpdates: any = {};

  if ('name' in updates && updates.name !== undefined) dbUpdates.name = updates.name.trim();
  if ('teamId' in updates) dbUpdates.team_id = updates.teamId;
  if ('tagId' in updates) dbUpdates.tag_id = updates.tagId;
  if ('stateGroupId' in updates) dbUpdates.state_group_id = updates.stateGroupId;
  if ('emoji' in updates) dbUpdates.emoji = updates.emoji;
  if ('color' in updates) dbUpdates.color = updates.color;
  if ('isOptimal' in updates) dbUpdates.is_optimal = updates.isOptimal;
  if ('sortOrder' in updates) dbUpdates.sort_order = updates.sortOrder;

  return dbUpdates;
};

export const dbRowToStateGroup = (row: any): StateGroup => ({
  id: row.id,
  teamId: row.team_id,
  name: row.name,
  createdAt: new Date(row.created_at),
});

export const dbRowToStateTransition = (row: any): StateTransition => ({
  id: row.id,
  teamId: row.team_id,
  tagId: row.tag_id,
  fromStateId: row.from_state_id,
  toStateId: row.to_state_id,
  fromHandle: row.from_handle ?? undefined,
  toHandle: row.to_handle ?? undefined,
  taskText: row.task_text,
  taskType: row.task_type,
  createdAt: new Date(row.created_at),
});

export const dbRowToStateHistory = (row: any): StateHistoryEntry => ({
  id: row.id,
  teamId: row.team_id,
  tagId: row.tag_id,
  fromStateId: row.from_state_id ?? null,
  toStateId: row.to_state_id,
  changedByMemberId: row.changed_by_member_id ?? null,
  changedByDeviceId: row.changed_by_device_id ?? null,
  triggerSource: row.trigger_source as StateHistoryTriggerSource,
  triggerReference: row.trigger_reference ?? null,
  createdAt: new Date(row.created_at),
});
